/**
 * "Контент" card — topics & keywords + content analysis (relevance, completeness, accuracy)
 */

import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  AnalysisSection,
  scoreTextColor,
  type XMLAnalysisResult,
} from './analysis-utils';

interface ContentSectionProps {
  result: XMLAnalysisResult;
}

function MetricRow({
  label,
  score,
  notes,
  noteVariant = 'muted',
}: {
  label: string;
  score: number;
  notes?: string[];
  noteVariant?: 'muted' | 'warning' | 'danger';
}) {
  const noteColor =
    noteVariant === 'danger'
      ? 'text-danger'
      : noteVariant === 'warning'
        ? 'text-warning'
        : 'text-fg-muted';
  return (
    <div>
      <div className="flex items-center justify-between rounded bg-bg-surface px-2.5 py-1.5 text-xs">
        <span className="text-fg-muted">{label}</span>
        <span className={cn('font-mono font-semibold', scoreTextColor(score))}>
          {score}
          <span className="text-fg-subtle">/100</span>
        </span>
      </div>
      {notes && notes.length > 0 && (
        <div className="mt-1 flex flex-col gap-0.5 px-2.5">
          {notes.map((n, idx) => (
            <div key={idx} className={cn('flex items-start gap-1 text-micro', noteColor)}>
              <span>•</span>
              <span>{n}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ContentSection({ result }: ContentSectionProps) {
  const { t } = useTranslation('records');

  const topic = result.content?.topic;
  const relevance = result.content?.relevance;
  const completeness = result.content?.completeness;
  const accuracy = result.content?.accuracy;

  const hasTopics = !!topic;
  const hasContentAnalysis = !!relevance || !!completeness || !!accuracy;

  if (!hasTopics && !hasContentAnalysis) return null;

  const summaryScore = relevance?.score ?? completeness?.score ?? accuracy?.score;

  return (
    <AnalysisSection
      title={t('records.analysisPanel.contentAnalysis', 'Content Analysis')}
      summary={
        summaryScore !== undefined ? (
          <span
            className={cn(
              'font-mono text-xs font-semibold',
              scoreTextColor(summaryScore)
            )}
          >
            {summaryScore}
          </span>
        ) : undefined
      }
    >
      {/* Topics & Keywords */}
      {hasTopics && (
        <div className="mb-3">
          {topic!.mainTopic && (
            <div className="mb-2 text-xs font-medium text-fg">{topic!.mainTopic}</div>
          )}
          {topic!.keywords && topic!.keywords.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {topic!.keywords.map((kw) => (
                <span
                  key={kw}
                  className="rounded-full bg-bg-subtle px-2.5 py-1 font-mono text-micro text-fg-secondary"
                >
                  {kw}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Content analysis metrics */}
      {hasContentAnalysis && (
        <div className="flex flex-col gap-1.5">
          {relevance && (
            <MetricRow
              label={t('records.analysisPanel.content.relevance', 'Relevance')}
              score={relevance.score}
              notes={relevance.feedback ? [relevance.feedback] : undefined}
            />
          )}
          {completeness && (
            <MetricRow
              label={t('records.analysisPanel.content.completeness', 'Completeness')}
              score={completeness.score}
              notes={completeness.missingInfo}
              noteVariant="warning"
            />
          )}
          {accuracy && (
            <MetricRow
              label={t('records.analysisPanel.content.accuracy', 'Accuracy')}
              score={accuracy.score}
              notes={accuracy.issues}
              noteVariant="danger"
            />
          )}
        </div>
      )}
    </AnalysisSection>
  );
}
